/**
 * Mixpanel Tracking Utilities
 *
 * This module provides helper functions for tracking user events consistently
 * across the application with standardized properties.
 *
 * Note: Mixpanel automatically provides session tracking via $session_id,
 * so we don't need to implement our own session ID.
 */

const LOGIN_TRACKED_KEY = 'inscope_login_tracked';

/**
 * Check if the login event has already been tracked for this browser session.
 * Uses sessionStorage so it resets when the tab is closed.
 * @returns {boolean} True if login has already been tracked
 */
export const hasLoginBeenTracked = () => {
  return sessionStorage.getItem(LOGIN_TRACKED_KEY) === 'true';
};

/**
 * Mark the login event as tracked for this browser session.
 */
export const markLoginAsTracked = () => {
  sessionStorage.setItem(LOGIN_TRACKED_KEY, 'true');
};

/**
 * Get base user and organization properties for tracking events.
 * @param {object} user - The current user object
 * @param {object} org - The current organization object
 * @returns {object} Base properties for tracking
 */
export const getBaseUserProperties = (user, org) => {
  return {
    org_id: org?.orgId || null,
    org_name: org?.orgName || null,
    user_id: user?.userId || null,
    user_email: user?.email || null,
    user_name:
      user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : null,
  };
};

/**
 * Get base document properties for tracking document-related events.
 * @param {object} documentData - The document data object
 * @param {object} user - The current user object
 * @param {object} org - The current organization object
 * @returns {object} Base properties including document details
 */
export const getBaseDocumentProperties = (documentData, user, org) => {
  return {
    ...getBaseUserProperties(user, org),
    document_id: documentData?.documentId || null,
    document_name: documentData?.name || null,
    document_type: documentData?.type || null,
    fiscal_year: documentData?.fiscalYear || null,
    quarter: documentData?.quarter || null,
  };
};
